import { useState } from "react";
import { Box, Button, CircularProgress, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import AppDialog from "../General/Dialog/AppDialog";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { retryEscritoUpload } from "../../services/LegalFileAttachmentService";

/**
 * Reintenta subir a SAC un escrito fallido, sobre el mismo registro (no lo
 * duplica). Si falta la sesión del abogado la pide y reintenta solo; si la
 * plantilla usa [REEMPLAZAR] y no hay dato guardado, lo pide.
 *
 * Mostrarlo solo cuando el estado del escrito en el expediente es "failed".
 */
export default function RetryEscritoButton({ attachmentId, legalFileId, escritoName, onDone, size = "medium" }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [needsReplacement, setNeedsReplacement] = useState(false);
  const [replacement, setReplacement] = useState("");
  const [sessionLawyer, setSessionLawyer] = useState(null);

  if (!hasPermission("cargar archivos adjuntos generados expediente")) {
    return null;
  }

  const close = () => {
    if (sending) return;
    setOpen(false);
    setNeedsReplacement(false);
    setReplacement("");
  };

  const submit = async () => {
    setSending(true);
    try {
      const res = await retryEscritoUpload(attachmentId, {
        legal_file_id: legalFileId,
        replacement: replacement.trim() || undefined,
      });
      showToast(res.data?.message ?? "Reintento iniciado.", "success");
      setOpen(false);
      setNeedsReplacement(false);
      setReplacement("");
      onDone?.();
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.status === "needs_session") {
        setSessionLawyer(data.lawyer);
        return;
      }
      if (data?.status === "needs_replacement") {
        setNeedsReplacement(true);
        return;
      }
      showToast(data?.message ?? "No se pudo reintentar el escrito.", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Tooltip title="Reintentar subida a SAC">
        <IconButton
          size={size}
          color="warning"
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 2, flexShrink: 0 }}
        >
          <ReplayRoundedIcon fontSize={size === "small" ? "small" : "medium"} />
        </IconButton>
      </Tooltip>

      <AppDialog
        open={open}
        onClose={close}
        title="Reintentar subida a SAC"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={close} disabled={sending}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={submit}
              disabled={sending || (needsReplacement && !replacement.trim())}
              startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <ReplayRoundedIcon />}
              sx={{ fontWeight: 800 }}
            >
              Reintentar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body2">
            Se vuelve a subir <b>{escritoName}</b> al portal con los mismos datos y archivos. Se reintenta sobre
            el mismo registro, así que no se duplica.
          </Typography>
          {needsReplacement && (
            <TextField
              label="Dato para [REEMPLAZAR]"
              size="small"
              fullWidth
              autoFocus
              value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
              helperText="La plantilla de este escrito lo necesita y no quedó guardado del primer intento."
            />
          )}
        </Box>
      </AppDialog>

      <LawyerSessionRequiredDialog
        open={Boolean(sessionLawyer)}
        onClose={() => setSessionLawyer(null)}
        lawyer={sessionLawyer}
        onCaptured={() => {
          setSessionLawyer(null);
          submit();
        }}
      />
    </>
  );
}
